<template>
  <v-card v-if="visible" :color="cardColor" variant="tonal" data-testid="overage-card">
    <v-card-item>
      <template #title>Labor overage</template>
      <template #append>
        <v-chip v-if="state.status === 'DECIDED'" size="small" :color="state.decision.decision === 'BILL' ? 'primary' : 'default'">
          {{ state.decision.decision === 'BILL' ? `Billed $${state.decision.billAmount.toLocaleString()}` : 'Absorbed' }}
        </v-chip>
        <v-chip v-else-if="state.status === 'STALE'" size="small" color="warning">Out of date</v-chip>
        <v-chip v-else-if="state.status === 'UNDECIDED'" size="small" color="error">Needs a decision</v-chip>
        <v-chip v-else size="small" color="success">On budget</v-chip>
      </template>
    </v-card-item>

    <v-card-text>
      <div class="d-flex flex-wrap" style="gap: 24px">
        <div>
          <div class="text-caption text-medium-emphasis">Max budget (all positions)</div>
          <div class="text-h6">${{ overage.baselineCost.toLocaleString() }}</div>
        </div>
        <div>
          <div class="text-caption text-medium-emphasis">Actual (approved)</div>
          <div class="text-h6">${{ overage.actualCost.toLocaleString() }}</div>
        </div>
        <div>
          <div class="text-caption text-medium-emphasis">Net overage</div>
          <div class="text-h6" :class="overage.overageCost > 0 ? 'text-error' : ''" data-testid="overage-cost">
            ${{ overage.overageCost.toLocaleString() }}
          </div>
        </div>
        <div v-if="overage.overageCost > 0">
          <div class="text-caption text-medium-emphasis">To client (+{{ overage.marginPct }}% margin)</div>
          <div class="text-h6" data-testid="overage-bill">${{ overage.billAmount.toLocaleString() }}</div>
        </div>
      </div>

      <p v-if="overage.overageCost === 0 && state.status === 'NONE'" class="text-body2 mt-3 mb-0">
        On budget overall — nothing to bill.
      </p>

      <v-table density="compact" class="mt-3 bg-transparent">
        <thead>
          <tr>
            <th>Position</th>
            <th class="text-right">Budget</th>
            <th class="text-right">Actual</th>
            <th class="text-right">Over / saved</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in overage.breakdown" :key="r.positionId">
            <td>{{ r.role }}<span v-if="r.label" class="text-medium-emphasis"> — {{ r.label }}</span></td>
            <td class="text-right">${{ r.budget.toLocaleString() }}</td>
            <td class="text-right">${{ r.actual.toLocaleString() }}</td>
            <td class="text-right" :class="r.difference > 0 ? 'text-error' : r.difference < 0 ? 'text-success' : ''">
              {{ r.difference > 0 ? '+' : r.difference < 0 ? '−' : '' }}${{ Math.abs(r.difference).toLocaleString() }}
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-alert v-if="unreviewed > 0" type="warning" variant="tonal" density="compact" class="mt-3">
        {{ unreviewed }} timesheet {{ unreviewed === 1 ? 'entry is' : 'entries are' }} still unreviewed — the overage may change once
        they're approved.
      </v-alert>

      <v-alert v-if="state.status === 'STALE'" type="warning" variant="tonal" density="compact" class="mt-3">
        The decision below was made on a ${{ state.decision.overageCost.toLocaleString() }} overage; it's now
        ${{ overage.overageCost.toLocaleString() }}.
        <template v-if="overage.overageCost === 0">
          There's nothing to decide on now.<template v-if="state.decision.decision === 'BILL'">
            If the overage line is still a draft on the change order, remove it from the Quote section.</template>
        </template>
        <template v-else-if="state.decision.decision === 'BILL'">
          Re-deciding updates the billed amount to the new figure — an earlier amount already approved on a quote isn't billed twice.
        </template>
      </v-alert>

      <div v-if="decision" class="text-body2 mt-3">
        <strong>{{ decision.decision === 'BILL' ? 'Billed' : 'Absorbed' }}</strong>
        <template v-if="decision.decision === 'BILL'"> ${{ decision.billAmount.toLocaleString() }} to the client (added to a change order)</template>
        on {{ formatDate(decision.decidedAt) }}<template v-if="decision.notes"> — “{{ decision.notes }}”</template>.
        <div v-if="decision.decision === 'ABSORB' && billedElsewhere" class="text-caption text-medium-emphasis">
          An earlier decision already billed part of this on an approved quote; that stays billed.
        </div>
      </div>

      <v-alert v-if="error || overageStore.error" type="error" closable class="mt-3" @click:close="dismissError">
        {{ error || overageStore.error }}
      </v-alert>
    </v-card-text>

    <v-card-actions v-if="canDecide && overage.overageCost > 0 && (state.status === 'UNDECIDED' || state.status === 'STALE')">
      <v-spacer />
      <v-btn variant="outlined" @click="openDialog('ABSORB')">Absorb</v-btn>
      <v-btn color="primary" variant="flat" @click="openDialog('BILL')">
        {{ state.status === 'STALE' ? 'Re-decide: bill client' : 'Bill client' }}
      </v-btn>
    </v-card-actions>
    <v-card-text v-else-if="!canDecide && state.status === 'UNDECIDED'" class="pt-0 text-body2">
      Waiting for a manager's decision.
    </v-card-text>

    <v-dialog v-model="dialogOpen" max-width="460">
      <v-card>
        <v-card-title>{{ mode === 'BILL' ? 'Bill client for the overage' : 'Absorb the overage' }}</v-card-title>
        <v-card-text>
          <p class="text-body2 mb-3">
            Labor ran ${{ overage.overageCost.toLocaleString() }} over the gig's overall budget of
            ${{ overage.baselineCost.toLocaleString() }}.
          </p>
          <template v-if="mode === 'BILL'">
            <v-text-field
              v-model.number="billAmount"
              type="number"
              min="0"
              step="0.01"
              prefix="$"
              label="Amount to bill the client"
              :hint="`Overage plus ${overage.marginPct}% margin = $${overage.billAmount.toLocaleString()}`"
              persistent-hint
              data-testid="bill-amount"
            />
            <v-textarea v-model="notes" label="Note (optional)" rows="2" auto-grow maxlength="1000" class="mt-3" />
            <p class="text-caption text-medium-emphasis mt-2">
              Adds a “Labor overage” line to a change order on the approved quote. Send and invoice it from the Quote section.
            </p>
          </template>
          <template v-else>
            <v-textarea v-model="notes" label="Reason (required)" rows="3" auto-grow maxlength="1000" data-testid="absorb-reason" />
            <p class="text-caption text-medium-emphasis mt-2">The cost stays with the company; the reason is kept in the audit trail.</p>
          </template>
          <v-alert v-if="dialogError" type="error" density="compact" class="mt-3">{{ dialogError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogOpen = false">Decide later</v-btn>
          <v-btn
            :color="mode === 'BILL' ? 'primary' : 'default'"
            variant="flat"
            :loading="overageStore.isSaving"
            data-testid="confirm-decision"
            @click="submit"
          >
            {{ mode === 'BILL' ? 'Bill client' : 'Absorb' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { ActualsSummary } from '@gigdriver/domain';
import { computeOverage, overageStatus, validateOverageDecision } from '@gigdriver/domain';
import { useOverageStore } from '../stores/overage';
import { useAuthStore } from '../stores/auth';
import { useOrganizationStore } from '../stores/organization';
import { useQuotesStore } from '../stores/quotes';

const props = defineProps<{
  orgId: string;
  gigId: string;
  actuals: ActualsSummary;
  marginPct: number;
}>();

const overageStore = useOverageStore();
const authStore = useAuthStore();
const orgStore = useOrganizationStore();
const quotesStore = useQuotesStore();

const overage = computed(() => computeOverage(props.actuals, props.marginPct));
const state = computed(() => overageStatus(overage.value, overageStore.decisions));
const decision = computed(() => (state.value.status === 'DECIDED' || state.value.status === 'STALE' ? state.value.decision : null));
const unreviewed = computed(() => props.actuals.totals.openCount + props.actuals.totals.pendingCount);
const canDecide = computed(() => ['owner', 'admin', 'manager'].includes(orgStore.myRole ?? ''));
// Hidden entirely until there's something to say: no approved time yet and no
// history means the Timesheets/budget sections already tell the story.
const visible = computed(() => props.actuals.totals.approvedCost > 0 || overageStore.decisions.length > 0);
const cardColor = computed(() => (state.value.status === 'UNDECIDED' ? 'error' : state.value.status === 'STALE' ? 'warning' : undefined));
// Overage already carried on an APPROVED quote (a locked line on the current
// change order) stays billed even if a later decision absorbs the rest.
const billedElsewhere = computed(
  () => quotesStore.currentQuote?.lineItems.some(l => !!l.overageDecisionId && l.locked) ?? false,
);

const error = ref<string | null>(null);
const dialogOpen = ref(false);
const dialogError = ref<string | null>(null);
const mode = ref<'BILL' | 'ABSORB'>('BILL');
const billAmount = ref<number>(0);
const notes = ref('');

function openDialog(m: 'BILL' | 'ABSORB') {
  mode.value = m;
  billAmount.value = overage.value.billAmount;
  notes.value = '';
  dialogError.value = null;
  dialogOpen.value = true;
}

// Called by GigDetail when a manager marks the gig COMPLETED with an
// undecided overage — offers the decision right then (still dismissible).
function promptDecision() {
  if (canDecide.value && state.value.status === 'UNDECIDED') openDialog('BILL');
}
defineExpose({ promptDecision });

async function submit() {
  dialogError.value = null;
  const uid = authStore.currentUser?.uid;
  if (!uid) return void (dialogError.value = 'Sign in again to record a decision.');
  const invalid = validateOverageDecision({ decision: mode.value, billAmount: billAmount.value, notes: notes.value });
  if (invalid) return void (dialogError.value = invalid);
  try {
    await overageStore.record(props.orgId, props.gigId, {
      decision: mode.value,
      billAmount: mode.value === 'BILL' ? billAmount.value : undefined,
      notes: notes.value,
      overage: overage.value,
      decidedBy: uid,
      supersedes: decision.value?.id,
    });
    dialogOpen.value = false;
    // Either decision can have changed the quote (a BILL stages/replaces the
    // overage line, an ABSORB removes a staged one) — refresh it.
    await quotesStore.loadQuote(props.orgId, props.gigId);
  } catch (err) {
    dialogError.value = err instanceof Error ? err.message : 'Failed to record decision';
    overageStore.error = null;
  }
}

function dismissError() {
  error.value = null;
  overageStore.error = null;
}

function formatDate(d?: Date): string {
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

watch(() => [props.orgId, props.gigId], () => (dialogOpen.value = false));
</script>
